import React, { useEffect, useContext, useState } from 'react';
import DatePicker from 'react-date-picker';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { FormFieldContext } from '../form-field/form-field.context';
import { ReactComponent as ActionIcDateRange24px } from '../../../assets/icons/action/ic_date_range_24px.svg';

let nextUniqueId;
nextUniqueId = 0;

const defaultIcons = {
  calendar: <ActionIcDateRange24px />
};

/**
 * @typedef Props
 * @prop {string} [className]
 * @prop {string} [calendarAriaLabel]
 * @prop {string} [format]
 * @prop {Date | Date[]} [value]
 * @prop {boolean} [todayMark]
 * @prop {(event: React.SyntheticEvent) => void} [onClick]
 * @prop {(x: Date) => void} [onChange]
 * @prop {(event: React.SyntheticEvent) => void} [onFocus]
 * @prop {(event: React.SyntheticEvent) => void} [onCalendarOpen]
 * @prop {(event: React.SyntheticEvent) => void} [onCalendarClose]
 * @prop {(event: React.SyntheticEvent) => void} [onKeyDown]
 * @prop {boolean} [range]
 * @prop {boolean} [disabled]
 * @prop {boolean} [required]
 * @prop {{calendar: React.ReactNode}} [icons]
 * @prop {Date} [minDate]
 * @prop {Date} [maxDate]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifDatePicker = (props) => {
  const {
    className = '',
    calendarAriaLabel = 'Calendar',
    format = 'y-MM-dd',
    value: valueProp = null,
    todayMark = false,
    onClick = () => null,
    onChange = () => null,
    onFocus = () => null,
    onCalendarOpen = () => null,
    onCalendarClose = () => null,
    onKeyDown = () => null,
    range = false,
    disabled = false,
    required = false,
    icons: userIcons = {},
    minDate = null,
    maxDate = null,
    ...other
  } = props;
  const formField = useContext(FormFieldContext);
  const [isOpen, setIsOpen] = useState(false);

  // act as controlled component through valueProp or
  // act as uncontrolled component through formField.value
  const value = valueProp || formField.value || null;

  const icons = {
    ...defaultIcons,
    ...userIcons
  };

  useEffect(() => {
    formField.setFormElementType('dateInput');
    formField.setId(`motif-date-picker-${nextUniqueId++}`);
  }, []);

  useEffect(() => {
    formField.setHasContent(value && typeof value === 'object');
  }, [value]);

  // keep formField state in sync with controlled values
  useEffect(() => {
    formField.setValue(valueProp);
  }, [valueProp]);

  useEffect(() => {
    formField.setRequired(required);
  }, [required]);

  useEffect(() => {
    formField.setDisabled(disabled);
  }, [disabled]);

  useEffect(() => {
    setIsOpen(formField.isFocused);
  }, [formField.isFocused]);

  const handleChange = date => {
    // if uncontrolled component, set formField value
    // otherwise useEffect above will update formField value
    if (!valueProp) {
      formField.setValue(date);
    }
    onChange(date);
  };

  const handleFocus = event => {
    formField.toggleFocus(true);
    onFocus(event);
  };

  const handleClick = event => {
    if (disabled) {
      return;
    }

    formField.toggleFocus(true);
    onClick(event);
  };

  const handleCalendarOpen = event => {
    onCalendarOpen(event);
  };

  const handleCalendarClose = event => {
    formField.toggleFocus(false);
    onCalendarClose(event);
  };

  const handleKeyDown = event => {
    onKeyDown(event);
  };

  const classes = classNames({
    'motif-date-picker': true,
    'motif-date-picker-has-value': formField.hasContent || value,
    'motif-date-picker-invalid': formField.invalid,
    'motif-date-picker-not-focused': !formField.isFocused,
    'motif-date-picker-today-highlight': todayMark,
    [className]: true
  });

  const datePickerProps = {
    name: formField.id,
    calendarAriaLabel,
    format,
    disabled,
    formatShortWeekday: (locale, date) => date.toLocaleString(locale, { weekday: 'narrow' }),
    next2Label: null,
    prev2Label: null,
    clearIcon: null,
    calendarClassName: 'motif-calendar',
    calendarIcon: <span className="motif-calendar-icon">{icons.calendar}</span>,
    className: classes,
    onFocus: handleFocus,
    onChange: handleChange,
    onCalendarClose: handleCalendarClose,
    onCalendarOpen: handleCalendarOpen,
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    value,
    isOpen,
    yearAriaLabel: 'Year',
    monthAriaLabel: 'Month',
    dayAriaLabel: 'Day',
    minDate,
    maxDate,
    ...other
  };

  return (
    <div className="motif-date-picker-wrapper" data-motif-date-picker>
      {range ? <DateRangePicker {...datePickerProps} /> : <DatePicker {...datePickerProps} />}
    </div>
  );
};

MotifDatePicker.propTypes = {
  calendarAriaLabel: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  format: PropTypes.string,
  icons: PropTypes.shape({
    calendar: PropTypes.node
  }),
  range: PropTypes.bool,
  required: PropTypes.bool,
  nativeInputAriaLabel: PropTypes.string,
  yearAriaLabel: PropTypes.string,
  monthAriaLabel: PropTypes.string,
  dayAriaLabel: PropTypes.string,
  todayMark: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.arrayOf(PropTypes.instanceOf(Date))
  ]),
  onFocus: PropTypes.func,
  onChange: PropTypes.func,
  onCalendarOpen: PropTypes.func,
  onCalendarClose: PropTypes.func,
  onClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date)
};

export { MotifDatePicker };
